/**
 * Cross-project layer copy/move UI (WP-C).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import { gtwinClient } from '@/app/gtwinClientSingleton';
import { fetchGtwinList, type GtwinListEntry } from './gtwinListClient';

export interface LayerTransferPanelProps {
  sourceGtwinId: string;
  selectedItemIds: ReadonlyArray<string>;
  onTransferComplete: () => void;
}

export function LayerTransferPanel({
  sourceGtwinId,
  selectedItemIds,
  onTransferComplete,
}: LayerTransferPanelProps) {
  const [twins, setTwins] = useState<GtwinListEntry[]>([]);
  const [targetGtwinId, setTargetGtwinId] = useState('');
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchGtwinList();
        if (!cancelled) {
          setTwins(list.filter((t) => t.id !== sourceGtwinId));
          setListError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setListError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceGtwinId]);

  const runTransfer = useCallback(
    async (deleteFromSource: boolean) => {
      if (!targetGtwinId || selectedItemIds.length === 0) return;
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const res = await gtwinClient.scene.items.copyFrom(targetGtwinId, {
          sourceGtwinId,
          itemIds: [...selectedItemIds],
          deleteFromSource,
        });
        setResult(
          `${deleteFromSource ? 'Moved' : 'Copied'} ${res.copied} layer(s) to ${targetGtwinId}.`,
        );
        onTransferComplete();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [onTransferComplete, selectedItemIds, sourceGtwinId, targetGtwinId],
  );

  const canAct = selectedItemIds.length > 0 && targetGtwinId.length > 0 && !loading;

  return (
    <section
      data-testid="layer-transfer-panel"
      aria-label="Copy or move layers"
      style={{
        borderTop: tokens.border.thin,
        padding: tokens.space[3],
        background: tokens.color.surface[1],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <h2
        style={{
          margin: `0 0 ${tokens.space[2]}px`,
          fontSize: tokens.font.size.sm,
          fontWeight: tokens.font.weight.semibold,
          color: tokens.color.ink.primary,
        }}
      >
        Transfer layers
      </h2>
      <p
        style={{
          margin: `0 0 ${tokens.space[2]}px`,
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
        }}
      >
        {selectedItemIds.length === 0
          ? 'Check layers in the tree to transfer.'
          : `${selectedItemIds.length} layer(s) selected`}
      </p>
      {listError ? (
        <p style={{ color: tokens.color.semantic.danger, fontSize: tokens.font.size.xs }}>
          {listError}
        </p>
      ) : null}
      <label
        style={{
          display: 'block',
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
          marginBottom: tokens.space[2],
        }}
      >
        Target project
        <select
          data-testid="transfer-target-select"
          value={targetGtwinId}
          onChange={(e) => setTargetGtwinId(e.target.value)}
          style={{
            display: 'block',
            width: '100%',
            marginTop: tokens.space[1],
            padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
            border: tokens.border.thin,
            borderRadius: tokens.radius.sm,
            background: tokens.color.surface[0],
            color: tokens.color.ink.primary,
            fontSize: tokens.font.size.sm,
          }}
        >
          <option value="">Select target twin…</option>
          {twins.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label} ({t.id.slice(0, 8)}…)
            </option>
          ))}
        </select>
      </label>
      <div style={{ display: 'flex', gap: tokens.space[2], flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="transfer-copy"
          disabled={!canAct}
          onClick={() => void runTransfer(false)}
          style={actionBtnStyle(canAct)}
        >
          Copy
        </button>
        <button
          type="button"
          data-testid="transfer-move"
          disabled={!canAct}
          onClick={() => void runTransfer(true)}
          style={actionBtnStyle(canAct)}
        >
          Move
        </button>
      </div>
      {result ? (
        <p
          role="status"
          style={{
            marginTop: tokens.space[2],
            fontSize: tokens.font.size.xs,
            color: tokens.color.semantic.success,
          }}
        >
          {result}{' '}
          {targetGtwinId ? (
            <Link
              to={`/studio/t/${targetGtwinId}`}
              style={{ color: tokens.color.accent.primary }}
            >
              Open target →
            </Link>
          ) : null}
        </p>
      ) : null}
      {error ? (
        <p role="alert" style={{ marginTop: tokens.space[2], fontSize: tokens.font.size.xs, color: tokens.color.semantic.danger }}>
          {error}
        </p>
      ) : null}
    </section>
  );
}

function actionBtnStyle(enabled: boolean): CSSProperties {
  return {
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    borderRadius: tokens.radius.sm,
    border: tokens.border.thin,
    background: enabled ? tokens.color.surface[2] : tokens.color.surface[0],
    color: enabled ? tokens.color.ink.primary : tokens.color.ink.muted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: tokens.font.size.sm,
  };
}
